/**
 * Quote detail view (19.3, 19.4, 19.5).
 * BOM lines with confidence badges, refresh pricing, convert to job.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fetchQuote, refreshQuotePricing, convertToJob } from '../../api/quotes';
import { SlideOutPanel, Button, LoadingSpinner, ProgressBar } from '../common';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

const CONFIDENCE_COLORS = {
  green: { dot: 'bg-green-500', label: 'Fresh', bg: '' },
  yellow: { dot: 'bg-yellow-500', label: 'Aging', bg: '' },
  red: { dot: 'bg-red-500', label: 'Stale', bg: 'bg-red-50/50' },
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-yellow-100 text-yellow-700',
  converted: 'bg-purple-100 text-purple-700',
};

export default function QuoteDetailView({ quoteId, open, onClose, onRefreshList }) {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshReport, setRefreshReport] = useState(null);
  const [converting, setConverting] = useState(false);

  const loadQuote = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const data = await fetchQuote(quoteId);
      setQuote(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    if (open && quoteId) {
      loadQuote();
      setRefreshReport(null);
    }
  }, [open, quoteId, loadQuote]);

  async function handleRefreshPricing() {
    setRefreshing(true);
    try {
      const data = await refreshQuotePricing(quoteId);
      setRefreshReport(data._refresh_report || null);
      // Reload quote with updated pricing
      const updated = await fetchQuote(quoteId);
      setQuote(updated);
      onRefreshList?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleConvertToJob() {
    if (!confirm('Convert this quote to a job? This will create a new job from the quote.')) return;
    setConverting(true);
    try {
      const job = await convertToJob(quoteId);
      onRefreshList?.();
      onClose?.();
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  }

  return (
    <SlideOutPanel
      open={open}
      onClose={onClose}
      title={quote ? `Quote #${quote.quote_number}` : 'Quote Detail'}
      width="max-w-2xl"
    >
      {loading ? (
        <div className="flex justify-center py-10"><LoadingSpinner /></div>
      ) : !quote ? (
        <p className="text-gray-500">Quote not found.</p>
      ) : (
        <div className="space-y-5">
          {/* Header info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoField label="Customer" value={quote.customer_name || quote.customer || '—'} />
            <InfoField label="Parent Item" value={quote.parent_item_display || quote.parent_item || '—'} />
            <InfoField label="Quantity" value={quote.quantity} />
            <InfoField label="Status">
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[quote.quote_status] || 'bg-gray-100 text-gray-600'}`}>
                {quote.quote_status}
              </span>
            </InfoField>
            <InfoField label="Sell Price" value={fmtCurrency(quote.total_sell_price)} />
            <InfoField label="Est. Parts Cost" value={fmtCurrency(quote.estimated_parts_cost)} />
            <InfoField label="Est. Margin" value={quote.estimated_margin != null ? `${Number(quote.estimated_margin).toFixed(1)}%` : '—'} />
            <InfoField label="Projected Delivery" value={fmtDate(quote.projected_delivery_date)} />
            <InfoField label="Created" value={fmtDate(quote.created_at)} />
            <InfoField label="Valid Until" value={fmtDate(quote.valid_until)} />
          </div>

          {/* Pricing confidence bar */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 font-medium">Pricing Confidence</span>
              <span className="text-xs text-gray-500">
                {quote.stale_items_count ?? 0} stale items
              </span>
            </div>
            <ProgressBar value={quote.pricing_confidence_pct || 0} height="h-4" />
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              loading={refreshing}
              onClick={handleRefreshPricing}
            >
              Refresh Pricing
            </Button>
            {quote.quote_status !== 'converted' && !quote.converted_to_job && (
              <Button
                size="sm"
                loading={converting}
                onClick={handleConvertToJob}
              >
                Convert to Job
              </Button>
            )}
            {quote.converted_to_job && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { onClose?.(); navigate(`/jobs/${quote.converted_to_job}`); }}
              >
                View Job
              </Button>
            )}
          </div>

          {/* Refresh report */}
          {refreshReport && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <h4 className="font-medium text-blue-700 mb-1">Pricing Refresh Report</h4>
              <div className="text-blue-600 text-xs space-y-0.5">
                {refreshReport.updated != null && <div>Lines updated: {refreshReport.updated}</div>}
                {refreshReport.unchanged != null && <div>Lines unchanged: {refreshReport.unchanged}</div>}
                {refreshReport.errors != null && <div>Errors: {refreshReport.errors}</div>}
              </div>
            </div>
          )}

          {/* BOM Lines with confidence */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">
              BOM Lines ({quote.lines?.length || 0})
            </h3>
            {(!quote.lines || quote.lines.length === 0) ? (
              <p className="text-sm text-gray-400">No lines in this quote.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-2 py-1.5 w-6" />
                      <th className="px-2 py-1.5">Part</th>
                      <th className="px-2 py-1.5">Vendor Part</th>
                      <th className="px-2 py-1.5 text-right">Qty/Parent</th>
                      <th className="px-2 py-1.5 text-right">Qty Ext.</th>
                      <th className="px-2 py-1.5 text-right">Unit Cost</th>
                      <th className="px-2 py-1.5 text-right">Last Quoted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quote.lines.map((line) => {
                      const conf = CONFIDENCE_COLORS[line.pricing_confidence] || CONFIDENCE_COLORS.red;
                      return (
                        <tr
                          key={line.id}
                          className={`hover:bg-gray-50 ${line.is_stale ? 'bg-red-50/50' : ''}`}
                        >
                          <td className="px-2 py-1.5">
                            <span
                              className={`inline-block w-2.5 h-2.5 rounded-full ${conf.dot}`}
                              title={conf.label}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-medium text-foreground">
                            {line.part_display || line.part || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500">
                            {line.vendor_part_display || line.vendor_part || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right">{line.qty_per_parent}</td>
                          <td className="px-2 py-1.5 text-right">{line.qty_extended}</td>
                          <td className="px-2 py-1.5 text-right">
                            {line.estimated_unit_cost != null
                              ? fmtCurrency(line.estimated_unit_cost)
                              : <span className="text-red-500 text-xs">No price</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right text-gray-400 text-xs">
                            {fmtDate(line.last_quoted_date)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Notes */}
          {quote.notes && (
            <div>
              <h4 className="text-xs text-gray-500 font-medium mb-1">Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}
        </div>
      )}
    </SlideOutPanel>
  );
}

function InfoField({ label, value, children }) {
  return (
    <div>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="font-medium text-foreground">{children || value}</dd>
    </div>
  );
}
